"use client";

import React from "react";
import { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import CloseIcon from "Components/Icons/CloseIcon";
import { MagnifyingIcon } from "Components/Icons/MagnifyingIcon";

export interface Option {
    value: string
    label: string
}

export interface GroupedOption {
    label: string
    options: Option[]
}

interface GroupedMultiSelectProps {
    label?: string
    options: GroupedOption[]
    value: Option[]
    onChange: (value: Option[]) => void
    placeholder?: string
}

export function GroupedMultiSelect({
                                       label,
                                       options,
                                       value,
                                       onChange,
                                       placeholder = "Select...",
                                   }: GroupedMultiSelectProps) {
    const [show, setShow] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(options.map((g) => g.label)));
    const dropdownRef = useRef<HTMLDivElement>(null);

    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;

        const term = searchTerm.toLowerCase();
        return options
            .map((group) => {
                const groupMatches = group.label.toLowerCase().includes(term);
                const matchingOptions = group.options.filter((opt) =>
                    opt.label.toLowerCase().includes(term)
                );

                if (groupMatches || matchingOptions.length > 0) {
                    return {
                        ...group,
                        options: groupMatches ? group.options : matchingOptions,
                    };
                }
                return null;
            })
            .filter((g): g is GroupedOption => g !== null);
    }, [options, searchTerm]);

    useEffect(() => {
        if (searchTerm) {
            setExpandedGroups(new Set(filteredOptions.map((g) => g.label)));
        }
    }, [filteredOptions, searchTerm]);

    useEffect(() => {
        if (!show) {
            setSearchTerm("");
        }
    }, [show]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShow(false);
            }
        };

        if (show) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [show]);

    const toggleGroup = (groupLabel: string) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(groupLabel)) {
                next.delete(groupLabel);
            } else {
                next.add(groupLabel);
            }
            return next;
        });
    };

    const isSelected = (option: Option) => {
        return value.some((v) => v.value === option.value);
    };

    const toggleOption = (option: Option) => {
        if (isSelected(option)) {
            onChange(value.filter((v) => v.value !== option.value));
        } else {
            onChange([...value, option]);
        }
    };

    const removeOption = (option: Option, e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(value.filter((v) => v.value !== option.value));
    };

    return (
        <div style={{ width: "100%" }} ref={dropdownRef}>
            {label && (
                <label className="form-label fw-bold" style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                    {label}
                </label>
            )}

            <div style={{ position: "relative" }}>
                <div
                    className="form-control d-flex align-items-center justify-content-between"
                    style={{
                        minHeight: "40px",
                        cursor: "pointer",
                        padding: ".5rem",
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        border: "1px solid #c2c2c2",
                        boxShadow: "0px 1px 2px rgba(5, 5, 5, 0.04)",
                        fontFamily: "Nunito Sans, sans-serif",
                        fontSize: "14px",
                        color: "#141414",
                    }}
                    onClick={() => setShow(!show)}
                >
                    <div className="d-flex flex-wrap gap-2 flex-grow-1" style={{width: "91%"}}>
                        {value.length === 0 ? (
                            <span style={{ color: "#8a8a8a", fontFamily: "Nunito Sans, sans-serif", fontSize: "14px" }}>{placeholder}</span>
                        ) : (
                            value.map((option) => (
                                <span
                                    key={option.value}
                                    className="badge d-inline-flex align-items-center gap-2"
                                    style={{
                                        backgroundColor: "#D6D6D6",
                                        color: "hsl(0, 0%, 20%)",
                                        border: "1px solid #c2c2c2",
                                        borderRadius: "30px",
                                        fontSize: "85%",
                                        fontFamily: "Nunito Sans, sans-serif",
                                        fontWeight: 400,
                                        height: "27.84px",
                                        padding: "0 6px",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        maxWidth: "calc(100% - 20px)",
                                        flexShrink: 1,
                                    }}
                                >
                                    <span
                                        style={{
                                            marginRight: 4,
                                            display: "inline-block",
                                            maxWidth: "100px", // Slightly increased from 26px to ensure proper truncation
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            minWidth: 0,
                                            verticalAlign: "middle", // Added for better vertical alignment
                                            lineHeight: "normal", // Added to ensure consistent text height
                                        }}
                                    >
                                        {option.label}
                                    </span>
                                    <button
                                        type="button"
                                        style={{
                                            padding: "0 2px",
                                            marginLeft: 2,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: 23,
                                            height: 23,
                                            border: "none",
                                            background: "transparent",
                                            cursor: "pointer",
                                            flex: "0 0 auto",
                                        }}
                                        onClick={(e) => removeOption(option, e)}
                                        aria-label="Remove"
                                    >
                                        <CloseIcon width={23} height={23} />
                                    </button>
                                </span>
                            ))
                        )}
                    </div>
                    <ChevronDown
                        style={{
                            marginLeft: "0.5rem",
                            transition: "transform 0.2s",
                            transform: show ? "rotate(180deg)" : "rotate(0deg)",
                            color: "#8a8a8a",
                            flexShrink: 0,
                        }}
                        size={20}
                    />
                </div>

                {show && (
                    <div
                        className="dropdown-menu show"
                        style={{
                            position: "absolute",
                            zIndex: 1050,
                            width: "100%",
                            marginTop: "0.25rem",
                            maxHeight: "400px",
                            overflowY: "auto",
                            padding: "0.25rem 0",
                            borderRadius: "0.5rem",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                            backgroundColor: "#fff",
                            border: "1px solid #e5e7eb",
                        }}
                    >
                        <div style={{ padding: "0.5rem 1rem", borderBottom: "1px solid #e5e7eb", position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 1, marginBottom: "0.25rem" }}>
                            <div className="d-flex align-items-center" style={{ position: "relative" }}>
                                <MagnifyingIcon style={{ position: "absolute", left: "0.75rem", pointerEvents: "none" }} />
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search labels..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{
                                        paddingLeft: "2.5rem",
                                        fontSize: "14px",
                                        fontFamily: "Nunito Sans, sans-serif",
                                        borderRadius: "6px",
                                        height: "36px",
                                        border: "1px solid #d1d5db"
                                    }}
                                    autoFocus
                                />
                                {searchTerm && (
                                    <button
                                        onClick={() => setSearchTerm("")}
                                        style={{
                                            position: "absolute",
                                            right: "0.75rem",
                                            background: "none",
                                            border: "none",
                                            cursor: "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            padding: 0
                                        }}
                                    >
                                        <CloseIcon width={16} height={16} />
                                    </button>
                                )}
                            </div>
                        </div>
                        {filteredOptions.length === 0 ? (
                            <div style={{ padding: "1rem", textAlign: "center", color: "#8a8a8a", fontSize: "14px", fontFamily: "Nunito Sans, sans-serif" }}>
                                No results found
                            </div>
                        ) : (
                            filteredOptions.map((group) => (
                                <div key={group.label}>
                                    <div
                                        className="dropdown-item d-flex align-items-center gap-2"
                                        style={{ cursor: "pointer", padding: "0.5rem 1rem", fontFamily: "Nunito Sans, sans-serif", fontSize: "14px", color: "#5c5c5c", fontWeight: 700 }}
                                        onClick={() => toggleGroup(group.label)}
                                    >
                                        {expandedGroups.has(group.label) ? (
                                            <ChevronDown size={16} style={{ color: "#8a8a8a" }} />
                                        ) : (
                                            <ChevronRight size={16} style={{ color: "#8a8a8a" }} />
                                        )}
                                        <span>{group.label}</span>
                                    </div>

                                    {expandedGroups.has(group.label) && (
                                        <div style={{ paddingLeft: "2rem" }}>
                                            {group.options.map((option) => (
                                                <div
                                                    key={option.value}
                                                    className="dropdown-item d-flex align-items-center gap-3"
                                                    style={{
                                                        cursor: "pointer",
                                                        padding: "0.5rem 1rem",
                                                        backgroundColor: isSelected(option) ? "#EAD7EA" : "transparent",
                                                        fontFamily: "Nunito Sans, sans-serif",
                                                        fontSize: "14px",
                                                        color: isSelected(option) ? "#792C76" : "#141414",
                                                        marginBottom: "0.25rem",
                                                        borderRadius: "0.5rem",
                                                    }}
                                                    onClick={() => toggleOption(option)}
                                                >
                                                    <div
                                                        className="form-check-input"
                                                        style={{
                                                            width: "1rem",
                                                            height: "1rem",
                                                            margin: 0,
                                                            backgroundColor: "#fff",
                                                            borderColor: isSelected(option) ? "#792C76" : "#c2c2c2",
                                                            cursor: "pointer",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            padding: 0,
                                                            lineHeight: 0,
                                                            boxSizing: "border-box",
                                                        }}
                                                    >
                                                        {isSelected(option) && (
                                                            <svg
                                                                style={{ color: "#792C76", display: "block", pointerEvents: "none" }}
                                                                width="12"
                                                                height="12"
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                                stroke="currentColor"
                                                            >
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <span>{option.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
